import { useSystemEventsStore } from '@/stores/systemEvents'
import { useWebSocketStore } from '@/stores/websocket'
import { useNotification } from './useNotification'
import { watch, onUnmounted, ref } from 'vue'
import { tGlobal } from './useI18n'

export function useSystemEvents() {
  const wsStore = useWebSocketStore()
  const store = useSystemEventsStore()
  const notification = useNotification()
  const t = tGlobal

  const interval = ref<ReturnType<typeof setInterval> | null>(null)
  const activeShutdownId = ref<string | null>(null)
  const processedEvents = new Set<string>()

  const startShutdownTimer = (delay: number, message: string) => {
    stopShutdownTimer()
    store.showShutdown(delay, message)
    notification.warn({ summary: t('platform.notifyBreakTimeout'), detail: message })

    interval.value = setInterval(() => {
      if (store.remainingTime <= 0) {
        stopShutdownTimer()
      } else {
        store.tick()
      }
    }, 1000)
  }

  const stopShutdownTimer = () => {
    if (interval.value) {
      clearInterval(interval.value)
      interval.value = null
    }
  }

  const stopWatcher = watch(
    () => wsStore.events,
    (events) => {
      if (!events?.length) return

      for (const event of events) {
        const shutdownId = event.data?.shutdownId?.value
        if (shutdownId) {
          const eventKey = `${shutdownId}:${event.type}`
          if (processedEvents.has(eventKey)) continue
          processedEvents.add(eventKey)

          if (event.type === 'notify-break-timeout') {
            activeShutdownId.value = shutdownId
            const delay = event.data?.delay?.value
            const message = event.data?.notification?.value
            if (delay) {
              startShutdownTimer(delay, message)
            }
          } else if (event.type === 'notify-break-timeout-cancel') {
            if (activeShutdownId.value === shutdownId) {
              stopShutdownTimer()
              store.hideShutdown()
              activeShutdownId.value = null
            }
            const message = event.data?.notification?.value
            notification.info({ summary: t('platform.notifyBreakTimeoutCancel'), detail: message })
          }
        }
      }
    },
    { deep: true, immediate: true }
  )

  onUnmounted(() => {
    stopWatcher()
    stopShutdownTimer()
  })
}
